import { mergeAttributes, Node } from "@tiptap/core"
import { TextSelection } from "@tiptap/pm/state"

declare module "@tiptap/core" {
	interface Commands<ReturnType> {
		cards: {
			insertCards: (count?: number) => ReturnType
			setCardColumns: (pos: number, cols: number) => ReturnType
		}
	}
}

export const MIN_CARD_COLUMNS = 1
export const MAX_CARD_COLUMNS = 4
export const DEFAULT_CARD_COLUMNS = 2

export function clampCardColumns(value: unknown): number {
	const parsed = Number(value)
	if (!Number.isFinite(parsed)) return DEFAULT_CARD_COLUMNS
	return Math.min(
		MAX_CARD_COLUMNS,
		Math.max(MIN_CARD_COLUMNS, Math.round(parsed)),
	)
}

/**
 * The title is its own text line, so a link on it is the card's link: the
 * portal stretches that link over the whole card, which is Mintlify's `href`.
 */
export const CardTitleExtension = Node.create({
	name: "cardTitle",
	content: "inline*",
	marks: "_",
	defining: true,

	parseHTML() {
		return [
			{ tag: "p[data-card-title]" },
			// A card stored before its title line carried the marker still leads with it.
			{ tag: 'div[data-type="card"] > p:first-child', priority: 60 },
		]
	},

	renderHTML({ HTMLAttributes }) {
		return [
			"p",
			mergeAttributes(HTMLAttributes, {
				"data-card-title": "",
				class: "pl-card-title",
			}),
			0,
		]
	},
})

export const CardExtension = Node.create({
	name: "card",
	content: "cardTitle block*",
	defining: true,
	isolating: true,

	parseHTML() {
		return [{ tag: 'div[data-type="card"]' }]
	},

	renderHTML({ HTMLAttributes }) {
		return [
			"div",
			mergeAttributes(HTMLAttributes, {
				"data-type": "card",
				class: "pl-card",
			}),
			0,
		]
	},
})

export const CardGroupExtension = Node.create({
	name: "cardGroup",
	group: "block",
	content: "card+",
	defining: true,
	isolating: true,

	addAttributes() {
		return {
			cols: {
				default: DEFAULT_CARD_COLUMNS,
				parseHTML: (element: HTMLElement) =>
					clampCardColumns(element.getAttribute("data-cols")),
				renderHTML: (attributes: Record<string, unknown>) => {
					const cols = clampCardColumns(attributes.cols)
					return { "data-cols": String(cols), style: `--cols: ${cols}` }
				},
			},
		}
	},

	parseHTML() {
		return [{ tag: 'div[data-type="card-group"]' }]
	},

	renderHTML({ HTMLAttributes }) {
		return [
			"div",
			mergeAttributes(HTMLAttributes, {
				"data-type": "card-group",
				class: "pl-card-group",
			}),
			0,
		]
	},

	addCommands() {
		return {
			insertCards:
				(count = DEFAULT_CARD_COLUMNS) =>
				({ chain, state }) => {
					const from = state.selection.from
					const cols = clampCardColumns(count)
					return chain()
						.insertContent({
							type: this.name,
							attrs: { cols },
							content: Array.from({ length: Math.max(1, count) }, () => ({
								type: "card",
								content: [{ type: "cardTitle" }, { type: "paragraph" }],
							})),
						})
						.command(({ tr, dispatch }) => {
							if (!dispatch) return true
							// The caret goes into the first title, where the author starts.
							let target = -1
							tr.doc.descendants((node, pos) => {
								if (target >= 0) return false
								if (node.type.name === "cardTitle" && pos >= from - 1) {
									target = pos
									return false
								}
								return true
							})
							if (target >= 0) {
								tr.setSelection(TextSelection.near(tr.doc.resolve(target + 1)))
							}
							return true
						})
						.run()
				},

			setCardColumns:
				(pos, cols) =>
				({ tr, state, dispatch }) => {
					const node = state.doc.nodeAt(pos)
					if (node?.type.name !== this.name) return false
					if (dispatch) {
						tr.setNodeMarkup(pos, undefined, {
							...node.attrs,
							cols: clampCardColumns(cols),
						})
					}
					return true
				},
		}
	},
})

export const CardExtensions = [
	CardGroupExtension,
	CardExtension,
	CardTitleExtension,
]
